/**
 * Classifies a UK postcode into a broad region, using the outward code's
 * area letters (e.g. "RG" from "RG1 2AB") against the Royal Mail list of
 * ~120 postcode areas. Used to restrict installers who only cover part of
 * the country (see Spirit Energy's serviceRegions in installers-data.ts).
 */

export type UkRegion =
  | "north-england"
  | "midlands"
  | "south-england"
  | "wales"
  | "scotland"
  | "northern-ireland"
  | "other";

const AREA_REGIONS: Record<string, UkRegion> = {
  // North West England
  BB: "north-england", BL: "north-england", CA: "north-england", CH: "north-england",
  CW: "north-england", FY: "north-england", L: "north-england", LA: "north-england",
  M: "north-england", OL: "north-england", PR: "north-england", SK: "north-england",
  WA: "north-england", WN: "north-england",
  // North East England
  DH: "north-england", DL: "north-england", NE: "north-england", SR: "north-england",
  TS: "north-england",
  // Yorkshire and the Humber
  BD: "north-england", DN: "north-england", HD: "north-england", HG: "north-england",
  HU: "north-england", HX: "north-england", LS: "north-england", S: "north-england",
  WF: "north-england", YO: "north-england",
  // East Midlands
  DE: "midlands", LE: "midlands", LN: "midlands", NG: "midlands", NN: "midlands",
  // West Midlands
  B: "midlands", CV: "midlands", DY: "midlands", HR: "midlands", ST: "midlands",
  SY: "midlands", TF: "midlands", WR: "midlands", WS: "midlands", WV: "midlands",
  // London
  E: "south-england", EC: "south-england", N: "south-england", NW: "south-england",
  SE: "south-england", SW: "south-england", W: "south-england", WC: "south-england",
  BR: "south-england", CR: "south-england", DA: "south-england", EN: "south-england",
  HA: "south-england", IG: "south-england", KT: "south-england", RM: "south-england",
  SM: "south-england", TW: "south-england", UB: "south-england",
  // South East England
  AL: "south-england", BN: "south-england", CT: "south-england", GU: "south-england",
  HP: "south-england", LU: "south-england", ME: "south-england", MK: "south-england",
  OX: "south-england", PO: "south-england", RG: "south-england", RH: "south-england",
  SG: "south-england", SL: "south-england", SO: "south-england", SS: "south-england",
  TN: "south-england", WD: "south-england",
  // South West England
  BA: "south-england", BH: "south-england", BS: "south-england", DT: "south-england",
  EX: "south-england", GL: "south-england", PL: "south-england", SN: "south-england",
  SP: "south-england", TA: "south-england", TQ: "south-england", TR: "south-england",
  // East of England
  CB: "south-england", CM: "south-england", CO: "south-england", IP: "south-england",
  NR: "south-england", PE: "south-england",
  // Wales
  CF: "wales", LD: "wales", LL: "wales", NP: "wales", SA: "wales",
  // Scotland
  AB: "scotland", DD: "scotland", DG: "scotland", EH: "scotland", FK: "scotland",
  G: "scotland", HS: "scotland", IV: "scotland", KA: "scotland", KW: "scotland",
  KY: "scotland", ML: "scotland", PA: "scotland", PH: "scotland", TD: "scotland",
  ZE: "scotland",
  // Northern Ireland
  BT: "northern-ireland",
  // Crown dependencies / other, not England/Wales/Scotland/NI
  GY: "other", JE: "other", IM: "other",
};

/**
 * Splits a postcode into outward (area + district) and inward (sector + unit)
 * codes, regardless of whether the input has a space. The inward code is
 * always exactly digit+letter+letter, so it anchors the split from the end;
 * without this, a no-space postcode like "PH15AB" would misparse as district
 * 15 (it's actually PH1 5AB, district 1) by greedily matching every leading
 * digit run as part of the district.
 */
function splitPostcode(postcode: string): { outward: string; inward: string } | null {
  const cleaned = postcode.trim().toUpperCase().replace(/\s+/g, "");
  if (cleaned.length < 5 || cleaned.length > 7) return null;
  const inward = cleaned.slice(-3);
  const outward = cleaned.slice(0, -3);
  if (!/^\d[A-Z]{2}$/.test(inward)) return null;
  if (!/^[A-Z]{1,2}\d[A-Z\d]?$/.test(outward)) return null;
  return { outward, inward };
}

/** Extracts the leading letter area code from a postcode ("RG1 2AB" -> "RG"). */
export function areaCode(postcode: string): string | null {
  const parts = splitPostcode(postcode);
  if (!parts) return null;
  const match = parts.outward.match(/^([A-Z]{1,2})/);
  return match ? match[1] : null;
}

/** Extracts the numeric district from a postcode ("SY23 1AB" -> 23, "PH1 2AB" -> 1). */
export function districtNumber(postcode: string): number | null {
  const parts = splitPostcode(postcode);
  if (!parts) return null;
  const match = parts.outward.match(/^[A-Z]{1,2}(\d+)/);
  return match ? parseInt(match[1], 10) : null;
}

/** Returns the region for a postcode, or null if it can't be parsed as a UK postcode. */
export function regionForPostcode(postcode: string): UkRegion | null {
  const area = areaCode(postcode);
  if (!area) return null;
  return AREA_REGIONS[area] ?? null;
}

/**
 * True only if the postcode is well-formed AND its area letters (e.g. "ZZ"
 * from "ZZ1 1AA") are one of the ~120 real Royal Mail postcode areas listed
 * in AREA_REGIONS above, catching structurally-valid but non-existent areas
 * that the format-only regex on the quote form's postcode field lets through.
 */
export function isKnownUkPostcodeArea(postcode: string): boolean {
  const area = areaCode(postcode);
  return area !== null && area in AREA_REGIONS;
}
